import {
  ArrowLeftOutlined,
  LoadingOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Modal } from 'antd';
import { debounce, get, isFunction } from 'lodash';
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import { trackFreeTextSearch } from '../../common/analytics/analyticsTracker';
import commonResource from '../../locales/th/common.json';
import { updateModalVisible } from '../../store/common/action';
import { ButtonWrapper } from '../searchResultPage/searchFilter/SearchFilter.style';
import { MobileSearchWrapper, SearchInputArea } from './MobileSearch.style';
import SearchSuggestion from './SearchSuggestion';
import { useSearchSuggestions } from '../../hooks/useSearchSuggestions';
import { WholeState as SearchWholeState } from '../../store/search/type';
import { WholeState as CommonWholeState } from '../../store/common/type';

const SEARCH_TRIGGER_CHAR_LENGTH = 2;
const SEARCH_DEBOUNCE = 500;

interface MobileSearchProps {
  updateSearchValue?: (value: string) => void;
}
const MobileSearch: React.FC<MobileSearchProps> = ({ updateSearchValue }) => {
  const [isRouteChanged, setIsRouteChanged] = useState(false);
  const [visible, setVisible] = useState(false);
  const currentSourcePage = useSelector((state: SearchWholeState) =>
    state.search !== undefined ? state.search.currentSourcePage : '',
  );
  const searchInput = useRef(null);
  const client = useApolloClient();
  const dispatch = useDispatch();
  const searchInputPlaceholderText = commonResource.searchInputPlaceholder;
  const searchButtonText = commonResource.search;
  const modalStyle = { height: '100vh' };
  const userInfo = useSelector((state: CommonWholeState) =>
    state !== undefined ? state.common.user : {},
  );

  // Use the hook
  const {
    initiateSearch,
    setShouldShowResult,
    setSearchInProgress,
    isSearchInProgress,
    shouldShowResult,
    suggestions,
  } = useSearchSuggestions({ client });

  const handleOnTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (isFunction(e.nativeEvent.stopImmediatePropagation)) {
      e.nativeEvent.stopImmediatePropagation();
    }
  };

  const handleCancel = () => {
    setVisible(false);
    setSearchInProgress(false);
    setShouldShowResult(false);
    searchInput.current.value = '';
    dispatch(updateModalVisible(false));
  };

  const searchFreeText = () => {
    setIsRouteChanged(true);
    const searchValue = get(searchInput, 'current.value');

    setTimeout(() => {
      if (!searchValue) {
        window.location.href = '/list/';
      } else {
        window.location.href = `/list/?q=${searchValue}`;
      }
    }, 1000);
  };

  const handleSearchFreeText = () => {
    trackFreeTextSearch(userInfo, currentSourcePage, searchInput.current.value);
    searchFreeText();
    handleCancel();
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.code === 'Enter') handleSearchFreeText();
  };

  const handleSearchValueChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    callback?: (value: string) => void,
  ) => {
    const searchValue = get(searchInput, 'current.value') || '';

    if (searchValue.length >= SEARCH_TRIGGER_CHAR_LENGTH) {
      initiateSearch(searchValue, isRouteChanged);
    } else {
      setShouldShowResult(false);
    }
    if (isFunction(callback)) callback(searchValue);
  };

  const debouncedSearchValueChange = debounce(
    handleSearchValueChange,
    SEARCH_DEBOUNCE,
  );

  const showModal = () => {
    setVisible(true);
    setTimeout(() => {
      searchInput.current.focus();
    });
    dispatch(updateModalVisible(true));
  };

  return (
    <MobileSearchWrapper data-test="mobile-search-button">
      <SearchOutlined onClick={() => showModal()} />

      <Modal
        data-test={'mobile-search'}
        visible={visible}
        onCancel={handleCancel}
        className="search-modal"
        closable={false}
        width="100%"
        style={modalStyle}
      >
        <SearchInputArea id="search-modal" onTouchMove={handleOnTouchMove}>
          {isSearchInProgress && (
            <LoadingOutlined onClick={() => handleCancel()} />
          )}
          {isSearchInProgress || (
            <ArrowLeftOutlined onClick={() => handleCancel()} />
          )}
          <input
            ref={searchInput}
            onFocus={debouncedSearchValueChange}
            onChange={(e) => debouncedSearchValueChange(e, updateSearchValue)}
            type="text"
            placeholder={searchInputPlaceholderText}
            onKeyUp={(e) => handleKeyUp(e)}
            data-test="mobile-search-input"
          />
        </SearchInputArea>

        <CSSTransition
          in={shouldShowResult}
          timeout={300}
          classNames="fade"
          unmountOnExit
        >
          <SearchSuggestion
            suggestions={suggestions}
            currentSourcePage={currentSourcePage}
          />
        </CSSTransition>

        <ButtonWrapper>
          <button onClick={() => handleSearchFreeText()}>
            {searchButtonText}
          </button>
        </ButtonWrapper>
      </Modal>
    </MobileSearchWrapper>
  );
};

export default MobileSearch;
